import React, { useState, useEffect, useRef } from 'react';
import mqtt from 'mqtt';
import axios from 'axios';

const TARGET_COLORS = ['#00ff88', '#ff6b6b', '#4ecdc4'];
const TRAIL_LENGTH = 30;
const MAX_RANGE = 8000;
const RADAR_SIZE = 400;
const CENTER_X = RADAR_SIZE / 2;
const CENTER_Y = RADAR_SIZE - 25;

const ROOMS = {
  radar1: { label: 'Office', color: '#00ff88', accent: 'rgba(0,255,136' },
  radar2: { label: 'Studio', color: '#4ecdc4', accent: 'rgba(78,205,196' },
};

const API_BASE = 'https://kidr78home.duckdns.org';
const AUTH = 'kSuIlUmNaTiIeO5';

function buildOccupancyTimeline(events, room) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const occEvents = events
    .filter(e => e.ts && e.ts.startsWith(todayStr) && e.event === 'occupancy' && e.occupancy != null && e.room === room)
    .sort((a, b) => a.ts.localeCompare(b.ts));

  if (!occEvents.length) return { steps: [], occupiedMinutes: 0, nowMinutes };

  const steps = occEvents.map(e => {
    const d = new Date(e.ts);
    return { minute: d.getHours() * 60 + d.getMinutes(), occ: e.occupancy };
  });

  let occupiedMs = 0;
  for (let i = 0; i < occEvents.length; i++) {
    if (occEvents[i].occupancy > 0) {
      const start = new Date(occEvents[i].ts).getTime();
      const end = (i + 1 < occEvents.length) ? new Date(occEvents[i + 1].ts).getTime() : now.getTime();
      occupiedMs += end - start;
    }
  }

  return { steps, occupiedMinutes: Math.round(occupiedMs / 60000), nowMinutes };
}

function RadarSvg({ radarData, trails, roomKey }) {
  const room = ROOMS[roomKey];
  const toSvg = (x_mm, y_mm) => {
    const scale = (RADAR_SIZE - 50) / (MAX_RANGE * 2);
    return { x: CENTER_X + x_mm * scale, y: CENTER_Y - y_mm * scale };
  };
  const rangeRings = [2000, 4000, 6000, 8000];

  return (
    <svg
      className="radar-svg"
      width={RADAR_SIZE}
      height={RADAR_SIZE}
      viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`}
    >
      <defs>
        <radialGradient id={`radarBg-${roomKey}`} cx="50%" cy="94%" r="100%">
          <stop offset="0%" stopColor={`${room.accent},0.05)`} />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
        {TARGET_COLORS.map((color, i) => (
          <radialGradient key={i} id={`glow-${roomKey}-${i}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={color} stopOpacity="0.8" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </radialGradient>
        ))}
      </defs>

      <rect width={RADAR_SIZE} height={RADAR_SIZE} fill="rgba(0,5,2,0.8)" rx="10" />
      <rect width={RADAR_SIZE} height={RADAR_SIZE} fill={`url(#radarBg-${roomKey})`} rx="10" />

      {/* Room label */}
      <text x={RADAR_SIZE / 2} y={18} textAnchor="middle" fill={room.color} fontSize="13" fontWeight="600" opacity="0.7">
        {room.label}
      </text>

      {rangeRings.map(range => {
        const r = (range / MAX_RANGE) * (RADAR_SIZE - 50) / 2;
        return (
          <g key={range}>
            <circle cx={CENTER_X} cy={CENTER_Y} r={r} fill="none" stroke={`${room.accent},0.12)`} strokeWidth="1" strokeDasharray="4,4" />
            <text x={CENTER_X + r + 3} y={CENTER_Y - 3} fill={`${room.accent},0.35)`} fontSize="9">
              {(range / 1000).toFixed(0)}m
            </text>
          </g>
        );
      })}

      <line x1={CENTER_X} y1={0} x2={CENTER_X} y2={RADAR_SIZE} stroke={`${room.accent},0.08)`} strokeWidth="1" />
      <line x1={0} y1={CENTER_Y} x2={RADAR_SIZE} y2={CENTER_Y} stroke={`${room.accent},0.08)`} strokeWidth="1" />

      <circle cx={CENTER_X} cy={CENTER_Y} r="5" fill={room.color} opacity="0.8" />
      <circle cx={CENTER_X} cy={CENTER_Y} r="9" fill="none" stroke={room.color} strokeWidth="1" opacity="0.4" />

      {/* Trails */}
      {[1, 2, 3].map(id => {
        const trail = trails[id] || [];
        if (trail.length < 2) return null;
        return (
          <g key={`trail-${id}`}>
            {trail.map((point, idx) => {
              if (idx === 0) return null;
              const prev = toSvg(trail[idx - 1].x, trail[idx - 1].y);
              const curr = toSvg(point.x, point.y);
              const opacity = (idx / trail.length) * 0.5;
              return (
                <line key={idx} x1={prev.x} y1={prev.y} x2={curr.x} y2={curr.y}
                  stroke={TARGET_COLORS[id - 1]} strokeWidth="2" opacity={opacity} />
              );
            })}
          </g>
        );
      })}

      {/* Targets */}
      {radarData.targets.map(target => {
        if (!target.present) return null;
        const pos = toSvg(target.x_mm, target.y_mm);
        const color = TARGET_COLORS[target.i - 1];
        return (
          <g key={target.i}>
            <circle cx={pos.x} cy={pos.y} r="18" fill={`url(#glow-${roomKey}-${target.i - 1})`} />
            <circle cx={pos.x} cy={pos.y} r="5" fill={color} />
            <circle cx={pos.x} cy={pos.y} r="9" fill="none" stroke={color} strokeWidth="1.5" opacity="0.6" />
            <text x={pos.x + 12} y={pos.y - 8} fill={color} fontSize="10" fontWeight="600" fontFamily="monospace">
              T{target.i}
            </text>
            <text x={pos.x + 12} y={pos.y + 5} fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="monospace">
              {(target.d_mm / 1000).toFixed(2)}m
            </text>
            {target.v_cms !== 0 && (
              <text x={pos.x + 12} y={pos.y + 16} fill="#f1c40f" fontSize="8" fontFamily="monospace">
                {target.v_cms > 0 ? '→' : '←'} {Math.abs(target.v_cms)}cm/s
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function OccupancyChart({ steps, nowMinutes, color, accent }) {
  if (!steps.length) return <div className="occ-no-data">No data today</div>;
  const maxOcc = Math.max(3, ...steps.map(s => s.occ));
  const toX = (min) => (min / 1440) * 760 + 30;
  const toY = (occ) => 80 - (occ / maxOcc) * 65;
  const endMin = nowMinutes || 0;

  let fillPath = `M ${toX(steps[0].minute)} 80`;
  let linePath = `M ${toX(steps[0].minute)} ${toY(steps[0].occ)}`;
  steps.forEach((step, i) => {
    const x = toX(step.minute);
    const y = toY(step.occ);
    if (i > 0) {
      fillPath += ` L ${x} ${toY(steps[i - 1].occ)} L ${x} ${y}`;
      linePath += ` L ${x} ${toY(steps[i - 1].occ)} L ${x} ${y}`;
    } else {
      fillPath += ` L ${x} ${y}`;
    }
  });
  const lastOcc = steps[steps.length - 1].occ;
  const endX = toX(endMin);
  fillPath += ` L ${endX} ${toY(lastOcc)} L ${endX} 80 Z`;
  linePath += ` L ${endX} ${toY(lastOcc)}`;

  return (
    <svg className="occ-timeline-svg" viewBox="0 0 800 100" preserveAspectRatio="xMidYMid meet">
      {Array.from({ length: 25 }, (_, h) => {
        const x = (h / 24) * 760 + 30;
        return (
          <g key={h}>
            <line x1={x} y1={5} x2={x} y2={82} stroke={`${accent},0.08)`} strokeWidth="1" />
            {h % 3 === 0 && (
              <text x={x} y={95} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace">
                {String(h).padStart(2, '0')}
              </text>
            )}
          </g>
        );
      })}
      <defs>
        <linearGradient id={`occFill-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`${accent},0.3)`} />
          <stop offset="100%" stopColor={`${accent},0.02)`} />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#occFill-${color})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" opacity="0.8" />
      <circle cx={endX} cy={toY(lastOcc)} r="3" fill={color} opacity="0.9" />
      <line x1={30} y1={80} x2={790} y2={80} stroke={`${accent},0.15)`} strokeWidth="1" />
    </svg>
  );
}

function RadarDashboard() {
  const [radar1, setRadar1] = useState({ t: 0, seq: 0, targets: [] });
  const [radar2, setRadar2] = useState({ t: 0, seq: 0, targets: [] });
  const [status1, setStatus1] = useState('Connecting...');
  const [status2, setStatus2] = useState('Connecting...');
  const [trails1, setTrails1] = useState({ 1: [], 2: [], 3: [] });
  const [trails2, setTrails2] = useState({ 1: [], 2: [], 3: [] });
  const [count1, setCount1] = useState(0);
  const [count2, setCount2] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventLimit, setEventLimit] = useState(50);
  const [statsEvents, setStatsEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState('all'); // all, radar1, radar2

  useEffect(() => { fetchEvents(); }, [eventLimit]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/radar/events?limit=1000`, { headers: { Authorization: AUTH } });
        setStatsEvents(res.data.events || []);
      } catch (err) { console.error('Stats fetch error:', err); }
    };
    fetchStats();
  }, []);

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/radar/events?limit=${eventLimit}`, { headers: { Authorization: AUTH } });
      setEvents(res.data.events || []);
    } catch (err) { console.error('Events fetch error:', err); }
    finally { setEventsLoading(false); }
  };

  const updateTrails = (setTrails, data) => {
    setTrails(prev => {
      const next = { ...prev };
      data.targets.forEach(t => {
        if (t.present) {
          const trail = [...(prev[t.i] || []), { x: t.x_mm, y: t.y_mm, ts: Date.now() }];
          next[t.i] = trail.slice(-TRAIL_LENGTH);
        } else if (prev[t.i] && prev[t.i].length > 0) {
          next[t.i] = prev[t.i].slice(1);
        }
      });
      return next;
    });
  };

  useEffect(() => {
    const client = mqtt.connect('wss://kidr78home.duckdns.org/mqtt');

    client.on('connect', () => {
      client.subscribe('home/sensors/radar/radar1');
      client.subscribe('home/sensors/radar/radar2');
    });

    client.on('error', () => {
      setStatus1('Error');
      setStatus2('Error');
    });

    client.on('message', (topic, message) => {
      try {
        const data = JSON.parse(message.toString());
        const present = data.targets.filter(t => t.present).length;

        if (topic === 'home/sensors/radar/radar1') {
          setRadar1(data);
          setCount1(present);
          setStatus1('Connected');
          updateTrails(setTrails1, data);
        } else if (topic === 'home/sensors/radar/radar2') {
          setRadar2(data);
          setCount2(present);
          setStatus2('Connected');
          updateTrails(setTrails2, data);
        }
      } catch (err) { console.error('Parse error:', err); }
    });

    return () => client.end();
  }, []);

  const getEventIcon = (event) => {
    switch (event) {
      case 'enter': return '🟢';
      case 'exit': return '🔴';
      case 'occupancy': return '👥';
      default: return '📡';
    }
  };

  const getEventDescription = (evt) => {
    const roomLabel = ROOMS[evt.room]?.label || evt.room || '?';
    if (evt.event === 'enter') {
      const dist = evt.d_mm ? `${(evt.d_mm / 1000).toFixed(1)}m` : '?';
      return `Target ${evt.target} entered ${roomLabel} at ${dist}`;
    }
    if (evt.event === 'exit') {
      const dur = evt.duration_s ? `${evt.duration_s.toFixed(1)}s` : '?';
      const dir = evt.direction || 'unknown';
      return `Target ${evt.target} exited ${roomLabel} ${dir} after ${dur}`;
    }
    if (evt.event === 'occupancy') {
      return evt.occupancy === 0 ? `${roomLabel} empty` : `${roomLabel}: ${evt.occupancy} ${evt.occupancy === 1 ? 'person' : 'people'}`;
    }
    return evt.event;
  };

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
  const formatOccTime = (mins) => { const h = Math.floor(mins / 60); const m = mins % 60; return h > 0 ? `${h}h ${m}m` : `${m}m`; };

  const occ1 = buildOccupancyTimeline(statsEvents, 'radar1');
  const occ2 = buildOccupancyTimeline(statsEvents, 'radar2');

  const filteredEvents = eventFilter === 'all' ? events : events.filter(e => e.room === eventFilter);

  const totalPresent = count1 + count2;

  return (
    <div className="radar-page">
      <style>{`
        .radar-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a0a1a 0%, #0d1b2a 50%, #1b2838 100%);
          padding: 80px 20px 40px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .radar-header { text-align: center; margin-bottom: 1.5rem; }
        .page-title {
          color: #00ff88; font-size: 2.2rem; font-weight: 700; margin-bottom: 0.5rem;
          text-shadow: 0 0 20px rgba(0,255,136,0.3);
        }
        .house-status {
          display: flex; justify-content: center; gap: 1.5rem; flex-wrap: wrap; margin-top: 0.75rem;
        }
        .room-badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.4rem 1rem; border-radius: 20px; font-size: 0.85rem; font-weight: 500;
        }
        .status-dot {
          width: 8px; height: 8px; border-radius: 50%; animation: blink 2s infinite;
        }
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

        .dual-radar-layout {
          display: flex; gap: 1.5rem; max-width: 1200px; margin: 0 auto;
          justify-content: center; flex-wrap: wrap;
        }
        .radar-column {
          display: flex; flex-direction: column; gap: 1rem; flex: 1; min-width: 300px; max-width: 500px;
        }
        .radar-container {
          background: rgba(0,20,10,0.6); border: 2px solid rgba(0,255,136,0.2);
          border-radius: 20px; padding: 1rem; overflow: hidden;
        }
        .radar-container.studio { border-color: rgba(78,205,196,0.2); }
        .radar-svg {
          display: block; margin: 0 auto; width: 100%; max-width: 400px; height: auto;
        }
        .info-card {
          background: rgba(255,255,255,0.05); backdrop-filter: blur(10px);
          border-radius: 16px; padding: 1rem; border: 1px solid rgba(0,255,136,0.15);
        }
        .info-card.studio { border-color: rgba(78,205,196,0.15); }
        .info-title {
          color: rgba(0,255,136,0.7); font-size: 0.7rem; text-transform: uppercase;
          letter-spacing: 2px; margin-bottom: 0.5rem;
        }
        .info-title.studio { color: rgba(78,205,196,0.7); }
        .target-count {
          font-size: 2.5rem; font-weight: 700; text-align: center;
        }
        .target-count-label {
          text-align: center; color: rgba(255,255,255,0.5); font-size: 0.75rem;
          text-transform: uppercase; letter-spacing: 2px;
        }
        .target-detail {
          display: flex; align-items: center; gap: 0.6rem; padding: 0.6rem;
          background: rgba(0,0,0,0.3); border-radius: 10px; margin-bottom: 0.4rem;
        }
        .target-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .target-info { flex: 1; }
        .target-label { color: rgba(255,255,255,0.6); font-size: 0.7rem; }
        .target-value { color: white; font-size: 0.9rem; font-weight: 600; font-family: monospace; }
        .target-velocity { color: rgba(255,255,255,0.5); font-size: 0.75rem; font-family: monospace; }
        .seq-info {
          display: flex; justify-content: space-between; color: rgba(255,255,255,0.4);
          font-size: 0.7rem; font-family: monospace;
        }

        .occupancy-section { max-width: 1200px; margin: 2rem auto 0; }
        .occ-row { display: flex; gap: 1.5rem; flex-wrap: wrap; }
        .occ-col { flex: 1; min-width: 300px; }
        .occ-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 0.5rem; flex-wrap: wrap; gap: 0.5rem;
        }
        .occ-title { font-size: 1rem; font-weight: 600; }
        .occupied-time {
          font-size: 0.8rem; font-family: monospace; padding: 0.2rem 0.6rem;
          border-radius: 10px;
        }
        .occ-chart-box {
          background: rgba(0,20,10,0.4); border: 1px solid rgba(0,255,136,0.12);
          border-radius: 14px; padding: 0.75rem; overflow-x: auto;
        }
        .occ-chart-box.studio { border-color: rgba(78,205,196,0.12); }
        .occ-timeline-svg { display: block; width: 100%; height: auto; }
        .occ-no-data { text-align: center; color: rgba(255,255,255,0.3); padding: 1.5rem; font-size: 0.85rem; }

        .events-section { max-width: 1200px; margin: 2rem auto 0; }
        .events-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 1rem; flex-wrap: wrap; gap: 0.75rem;
        }
        .events-title { color: #00ff88; font-size: 1.3rem; font-weight: 600; }
        .events-controls { display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; }
        .filter-btn, .limit-btn {
          background: rgba(0,255,136,0.1); border: 1px solid rgba(0,255,136,0.3);
          color: #00ff88; padding: 0.35rem 0.7rem; border-radius: 8px;
          cursor: pointer; font-size: 0.78rem; transition: background 0.2s;
        }
        .filter-btn:hover, .limit-btn:hover { background: rgba(0,255,136,0.2); }
        .filter-btn.active, .limit-btn.active { background: rgba(0,255,136,0.3); border-color: #00ff88; }
        .filter-btn.studio { color: #4ecdc4; border-color: rgba(78,205,196,0.3); background: rgba(78,205,196,0.1); }
        .filter-btn.studio:hover { background: rgba(78,205,196,0.2); }
        .filter-btn.studio.active { background: rgba(78,205,196,0.3); border-color: #4ecdc4; }
        .refresh-btn {
          background: rgba(0,255,136,0.15); border: 1px solid rgba(0,255,136,0.3);
          color: #00ff88; padding: 0.35rem 0.7rem; border-radius: 8px; cursor: pointer; font-size: 0.8rem;
        }
        .refresh-btn:hover { background: rgba(0,255,136,0.25); }
        .event-timeline { display: flex; flex-direction: column; }
        .event-row { display: flex; align-items: stretch; gap: 0.75rem; }
        .event-time-col { width: 75px; text-align: right; flex-shrink: 0; padding: 0.6rem 0; }
        .event-time { color: rgba(255,255,255,0.6); font-size: 0.78rem; font-family: monospace; }
        .event-date { color: rgba(255,255,255,0.3); font-size: 0.68rem; }
        .event-line-col { display: flex; flex-direction: column; align-items: center; width: 28px; flex-shrink: 0; }
        .event-dot { width: 11px; height: 11px; border-radius: 50%; flex-shrink: 0; margin-top: 0.75rem; z-index: 1; }
        .event-line { flex: 1; width: 2px; background: rgba(0,255,136,0.15); }
        .event-content {
          flex: 1; background: rgba(255,255,255,0.04); border-radius: 10px;
          padding: 0.6rem 0.9rem; margin: 0.2rem 0; border: 1px solid rgba(255,255,255,0.06);
        }
        .event-type { font-size: 0.82rem; font-weight: 600; margin-bottom: 0.2rem; }
        .event-type.enter { color: #00ff88; }
        .event-type.exit { color: #ff6b6b; }
        .event-type.occupancy { color: #4ecdc4; }
        .event-desc { color: rgba(255,255,255,0.6); font-size: 0.78rem; }
        .event-meta { color: rgba(255,255,255,0.3); font-size: 0.68rem; font-family: monospace; margin-top: 0.2rem; }
        .room-tag {
          display: inline-block; font-size: 0.65rem; padding: 0.1rem 0.4rem;
          border-radius: 4px; margin-left: 0.5rem; font-weight: 500;
        }
        .events-loading { text-align: center; color: rgba(0,255,136,0.5); padding: 2rem; }

        @media (max-width: 768px) {
          .radar-page { padding: 70px 10px 30px; }
          .page-title { font-size: 1.6rem; }
          .dual-radar-layout { flex-direction: column; align-items: stretch; }
          .radar-column { max-width: unset; }
          .radar-container { padding: 0.5rem; }
          .occ-row { flex-direction: column; }
          .event-time-col { width: 60px; }
        }
      `}</style>

      <div className="radar-header">
        <h1 className="page-title">📡 Radar Tracker</h1>
        <div className="house-status">
          <div className="room-badge" style={{ background: 'rgba(0,255,136,0.15)', color: '#00ff88' }}>
            <span className="status-dot" style={{ background: status1 === 'Connected' ? '#00ff88' : '#f1c40f' }} />
            Office {count1 > 0 ? `(${count1})` : ''}
          </div>
          <div className="room-badge" style={{ background: 'rgba(78,205,196,0.15)', color: '#4ecdc4' }}>
            <span className="status-dot" style={{ background: status2 === 'Connected' ? '#4ecdc4' : '#f1c40f' }} />
            Studio {count2 > 0 ? `(${count2})` : ''}
          </div>
          {totalPresent > 0 && (
            <div className="room-badge" style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}>
              🏠 {totalPresent} total
            </div>
          )}
        </div>
      </div>

      <div className="dual-radar-layout">
        {/* Office (radar1) */}
        <div className="radar-column">
          <div className="radar-container">
            <RadarSvg radarData={radar1} trails={trails1} roomKey="radar1" />
          </div>
          <div className="info-card">
            <div className="info-title">Office — Targets</div>
            <div className="target-count" style={{ color: '#00ff88', textShadow: '0 0 20px rgba(0,255,136,0.4)' }}>{count1}</div>
            <div className="target-count-label">{count1 === 0 ? 'Empty' : count1 === 1 ? 'Person detected' : 'People detected'}</div>
            {radar1.targets.filter(t => t.present).map(target => (
              <div key={target.i} className="target-detail" style={{ marginTop: '0.5rem' }}>
                <div className="target-dot" style={{ background: TARGET_COLORS[target.i - 1], boxShadow: `0 0 8px ${TARGET_COLORS[target.i - 1]}` }} />
                <div className="target-info">
                  <div className="target-label">Target {target.i}</div>
                  <div className="target-value">{(target.d_mm / 1000).toFixed(2)}m</div>
                  <div className="target-velocity">
                    X: {target.x_mm}mm  Y: {target.y_mm}mm
                    {target.v_cms !== 0 && ` • ${target.v_cms} cm/s`}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="info-card">
            <div className="info-title">Office — Telemetry</div>
            <div className="seq-info"><span>SEQ: {radar1.seq}</span><span>T: {radar1.t}ms</span></div>
          </div>
        </div>

        {/* Studio (radar2) */}
        <div className="radar-column">
          <div className="radar-container studio">
            <RadarSvg radarData={radar2} trails={trails2} roomKey="radar2" />
          </div>
          <div className="info-card studio">
            <div className="info-title studio">Studio — Targets</div>
            <div className="target-count" style={{ color: '#4ecdc4', textShadow: '0 0 20px rgba(78,205,196,0.4)' }}>{count2}</div>
            <div className="target-count-label">{count2 === 0 ? 'Empty' : count2 === 1 ? 'Person detected' : 'People detected'}</div>
            {radar2.targets.filter(t => t.present).map(target => (
              <div key={target.i} className="target-detail" style={{ marginTop: '0.5rem' }}>
                <div className="target-dot" style={{ background: TARGET_COLORS[target.i - 1], boxShadow: `0 0 8px ${TARGET_COLORS[target.i - 1]}` }} />
                <div className="target-info">
                  <div className="target-label">Target {target.i}</div>
                  <div className="target-value">{(target.d_mm / 1000).toFixed(2)}m</div>
                  <div className="target-velocity">
                    X: {target.x_mm}mm  Y: {target.y_mm}mm
                    {target.v_cms !== 0 && ` • ${target.v_cms} cm/s`}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="info-card studio">
            <div className="info-title studio">Studio — Telemetry</div>
            <div className="seq-info"><span>SEQ: {radar2.seq}</span><span>T: {radar2.t}ms</span></div>
          </div>
        </div>
      </div>

      {/* Occupancy Timelines */}
      <div className="occupancy-section">
        <div className="occ-row">
          <div className="occ-col">
            <div className="occ-header">
              <span className="occ-title" style={{ color: '#00ff88' }}>📊 Office — Today</span>
              {occ1.occupiedMinutes > 0 && (
                <span className="occupied-time" style={{ color: '#00ff88', background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.2)' }}>
                  🕐 {formatOccTime(occ1.occupiedMinutes)}
                </span>
              )}
            </div>
            <div className="occ-chart-box">
              <OccupancyChart steps={occ1.steps} nowMinutes={occ1.nowMinutes} color="#00ff88" accent="rgba(0,255,136" />
            </div>
          </div>
          <div className="occ-col">
            <div className="occ-header">
              <span className="occ-title" style={{ color: '#4ecdc4' }}>📊 Studio — Today</span>
              {occ2.occupiedMinutes > 0 && (
                <span className="occupied-time" style={{ color: '#4ecdc4', background: 'rgba(78,205,196,0.1)', border: '1px solid rgba(78,205,196,0.2)' }}>
                  🕐 {formatOccTime(occ2.occupiedMinutes)}
                </span>
              )}
            </div>
            <div className="occ-chart-box studio">
              <OccupancyChart steps={occ2.steps} nowMinutes={occ2.nowMinutes} color="#4ecdc4" accent="rgba(78,205,196" />
            </div>
          </div>
        </div>
      </div>

      {/* Event Log */}
      <div className="events-section">
        <div className="events-header">
          <span className="events-title">📋 Event Log</span>
          <div className="events-controls">
            <button className={`filter-btn ${eventFilter === 'all' ? 'active' : ''}`} onClick={() => setEventFilter('all')}>All</button>
            <button className={`filter-btn ${eventFilter === 'radar1' ? 'active' : ''}`} onClick={() => setEventFilter('radar1')}>Office</button>
            <button className={`filter-btn studio ${eventFilter === 'radar2' ? 'active' : ''}`} onClick={() => setEventFilter('radar2')}>Studio</button>
            <span style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 0.25rem' }} />
            {[50, 100, 200].map(n => (
              <button key={n} className={`limit-btn ${eventLimit === n ? 'active' : ''}`} onClick={() => setEventLimit(n)}>{n}</button>
            ))}
            <button className="refresh-btn" onClick={fetchEvents}>🔄</button>
          </div>
        </div>

        {eventsLoading ? (
          <div className="events-loading">Loading events...</div>
        ) : (
          <div className="event-timeline">
            {filteredEvents.map((evt, idx) => {
              const roomInfo = ROOMS[evt.room];
              const roomColor = roomInfo?.color || '#888';
              return (
                <div key={evt.id} className="event-row">
                  <div className="event-time-col">
                    <div className="event-time">{formatTime(evt.ts)}</div>
                    <div className="event-date">{formatDate(evt.ts)}</div>
                  </div>
                  <div className="event-line-col">
                    <div className="event-dot" style={{
                      background: evt.event === 'enter' ? '#00ff88' : evt.event === 'exit' ? '#ff6b6b' : '#4ecdc4'
                    }} />
                    {idx < filteredEvents.length - 1 && <div className="event-line" />}
                  </div>
                  <div className="event-content">
                    <div className={`event-type ${evt.event}`}>
                      {getEventIcon(evt.event)} {evt.event.toUpperCase()}
                      <span className="room-tag" style={{ background: `${roomColor}22`, color: roomColor, border: `1px solid ${roomColor}44` }}>
                        {roomInfo?.label || evt.room}
                      </span>
                    </div>
                    <div className="event-desc">{getEventDescription(evt)}</div>
                    {evt.event === 'enter' && evt.x_mm != null && (
                      <div className="event-meta">Position: ({evt.x_mm}, {evt.y_mm})mm</div>
                    )}
                    {evt.event === 'exit' && evt.dx_mm != null && (
                      <div className="event-meta">Displacement: ({evt.dx_mm}, {evt.dy_mm})mm</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default RadarDashboard;
